import { emptyFragment } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { fromArrayMaybe } from 'nonempty-list';
import { observer } from 'mobx-react';
import { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { teamUrl } from '../../../ClientRoutes';
import { findLink } from '../../../LinkyLinky';
import { TeamsStoreContext } from '../../../TeamsStore/TeamsStoreContext';
import { TeamMemberResource, TeamResource } from '../../../TeamsStore/Types';
import { NotTranslated, T } from '../../../Translations';
import AsModal from '../../Modal/AsModal';
import { useOpenable } from '../../Openable';
import {
  programCardBoxClassName,
  programCardBoxHoverClassName,
  programCardButtonClassName,
  ProgramCardFooterGroup,
} from '.';

function MemberInitials({ member }: { member: TeamMemberResource }) {
  const { profile } = member.payload;

  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
      <NotTranslated text={profile.payload.initials.getOrElseValue('')} />
    </span>
  );
}

// A member without a photo, or whose photo fails to load, shows their initials.
function MemberPhoto({ member }: { member: TeamMemberResource }) {
  const [failed, setFailed] = useState(false);

  return findLink('avatar', member.payload.profile.links)
    .filter(() => !failed)
    .map((avatar) => (
      <img
        src={avatar.href}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover"
        onError={() => {
          setFailed(true);
        }}
      />
    ))
    .getOrElse(() => <MemberInitials member={member} />);
}

function Member({ member }: { member: TeamMemberResource }) {
  const { profile } = member.payload;

  return (
    <li className="flex items-center gap-3" data-test-team-member={member.payload.id}>
      <MemberPhoto member={member} />
      <div className="min-w-0">
        <p className="m-0 font-semibold text-gray-900">
          <NotTranslated text={profile.payload.name.getOrElseValue(member.payload.email)} />
        </p>
        {profile.payload.organization
          .map((organization) => (
            <p className="m-0 text-sm text-gray-500">
              <NotTranslated text={organization} />
            </p>
          ))
          .getOrElse(emptyFragment)}
      </div>
    </li>
  );
}

function TeamBox({ team }: { team: TeamResource }) {
  const { openableState, open, close } = useOpenable();

  return (
    <>
      <button
        type="button"
        onClick={open}
        className={clsx(programCardBoxClassName, programCardBoxHoverClassName, 'max-w-60')}
        title={team.payload.name}
        data-intercom-target="dashboard-program-team-button"
        data-test-program-team={team.payload.id}
      >
        {/* The box has a max width, so a long team name ends with "..." and the full
            name shows on hover. */}
        <span className="min-w-0 truncate text-lg font-semibold text-gray-900">
          <NotTranslated text={team.payload.name} />
        </span>
      </button>
      <AsModal
        state={openableState}
        close={close}
        title={<NotTranslated text={team.payload.name} />}
      >
        {/* Only the members scroll, so a large team keeps the popup short and the button
            below stays in view. */}
        <ul className="m-0 flex max-h-[min(24rem,calc(100vh-18rem))] list-none flex-col gap-4 overflow-y-auto p-0 pr-2">
          {team.payload.teamMembers.map((member) => (
            <Member key={member.payload.id} member={member} />
          ))}
        </ul>
        <div className="mt-5 flex border-t border-gray-200 pt-5">
          <RouterLink
            to={teamUrl(team.payload.id)}
            className={programCardButtonClassName}
            data-intercom-target="dashboard-program-team-chat-link"
          >
            <T kind="Message Your Team" />
          </RouterLink>
        </div>
      </AsModal>
    </>
  );
}

interface Props {
  programId: number;
}

// A group in the card footer: the learner's team in this program. The box opens a popup
// with the team members and a link to the team chat. The teams store only holds teams with
// team chat turned on, so a program without one shows nothing. A student has one team per
// program; a coach can have more, and sees the first.
function ProgramCardTeam({ programId }: Props) {
  const teamsStore = useContext(TeamsStoreContext);

  return teamsStore.teams
    .andThen((teamsResource) =>
      fromArrayMaybe(teamsResource.payload.filter((team) => team.payload.courseId === programId)),
    )
    .map(({ first }) => (
      <ProgramCardFooterGroup label="Team">
        <TeamBox team={first} />
      </ProgramCardFooterGroup>
    ))
    .getOrElse(emptyFragment);
}

export default observer(ProgramCardTeam);
